/**
 * Date arithmetic for the interview calendar, in the viewer's own timezone.
 *
 * Kept apart from the component so it can be tested without rendering, and so
 * the one rule that matters is in one place: a day is local midnight to local
 * midnight, and the window sent to the API is built from those same midnights.
 * Build it in UTC instead and an evening interview in India lands on the next
 * day's cell.
 *
 * Weeks start on Monday, which is how the API's own weekly buckets are cut.
 */

/** Local midnight at the start of `date`'s day. */
export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** `date` moved by whole calendar days, so a DST change never shifts the hour. */
export function addDays(date, count) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);
}

/** The Monday on or before `date`. */
export function startOfWeek(date) {
  const day = startOfDay(date);
  // getDay(): Sunday is 0. Monday-based offset: Mon 0 … Sun 6.
  return addDays(day, -((day.getDay() + 6) % 7));
}

export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The days a view shows, and the window to ask the API for.
 *
 * A month is always six full weeks, so the grid never changes height as you
 * page through the year, and the spill days either side are real days with
 * their interviews on them rather than blanks.
 */
export function calendarRange(mode, anchor) {
  const first =
    mode === 'month'
      ? startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1))
      : startOfWeek(anchor);
  const length = mode === 'month' ? 42 : 7;
  const days = Array.from({ length }, (_, index) => addDays(first, index));
  return { days, start: first, end: addDays(first, length) };
}

/** The anchor one view earlier (`-1`) or later (`1`). */
export function shiftAnchor(mode, anchor, direction) {
  if (mode === 'month') {
    return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
  }
  return addDays(anchor, 7 * direction);
}

/** When an interview starts and ends, as Dates. */
export function interviewSpan(interview) {
  const start = new Date(interview.scheduled_at);
  return { start, end: new Date(start.getTime() + interview.duration_minutes * 60_000) };
}

/**
 * Does the interview touch this local day?
 *
 * Overlap, not "starts on": a round from 23:30 to 00:30 belongs to both days,
 * which is also how the API chooses what to return.
 */
export function occursOn(interview, day) {
  const { start, end } = interviewSpan(interview);
  const dayStart = startOfDay(day);
  // Next local midnight, not +24h: a DST day is 23 or 25 hours long.
  const dayEnd = addDays(dayStart, 1);
  return start < dayEnd && end > dayStart;
}

/** Interviews grouped onto the days of a view, each day earliest first. */
export function groupByDay(interviews, days) {
  return days.map((day) => ({
    day,
    interviews: interviews
      .filter((interview) => occursOn(interview, day))
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)),
  }));
}

/** "10:00" in the viewer's locale. */
export function timeLabel(date) {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** The heading over the grid: "October 2026", or "5 – 11 Oct 2026". */
export function rangeTitle(mode, anchor, days) {
  if (mode === 'month') {
    return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }
  const first = days[0];
  const last = days[days.length - 1];
  const sameMonth = first.getMonth() === last.getMonth();
  const head = first.toLocaleDateString(
    undefined,
    sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' },
  );
  const tail = last.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${head} – ${tail}`;
}
